import React, { useEffect, useState } from 'react';
import { apiService } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { Mail, Clock, CheckCircle, AlertCircle, MousePointerClick } from 'lucide-react';

type Reminder = {
  id: number;
  invoice_id: number;
  reminder_type: string;
  status: string;
  email_sent_to: string;
  subject?: string;
  message?: string;
  sent_at: string;
  opened_at?: string;
  clicked_at?: string;
  sent_by?: number;
  notes?: string;
};

type ReminderHistoryPanelProps = {
  invoiceId: number;
};

const getReminderTypeLabel = (type: string) => {
  switch (type) {
    case 'auto_1': return '1ère relance auto';
    case 'auto_2': return '2ème relance auto';
    case 'formal': return 'Relance formelle';
    case 'legal': return 'Mise en demeure';
    case 'manual': return 'Relance manuelle';
    default: return type;
  }
};

const getStatusConfig = (status: string) => {
  switch (status) {
    case 'sent':
      return { label: 'Envoyée', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', icon: Mail };
    case 'opened':
      return { label: 'Ouverte', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircle };
    case 'clicked':
      return { label: 'Cliquée', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400', icon: MousePointerClick };
    case 'bounced':
      return { label: 'Rebond', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: AlertCircle };
    default:
      return { label: status, color: 'bg-slate-100 text-slate-700 dark:bg-slate-900/30 dark:text-slate-400', icon: Mail };
  }
};

export const ReminderHistoryPanel: React.FC<ReminderHistoryPanelProps> = ({ invoiceId }) => {
  const { toast } = useToast();
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [busy, setBusy] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const load = async () => {
    try {
      setBusy(true);
      const data = await apiService.get(`/invoices/${invoiceId}/reminder-history`);
      setReminders(Array.isArray(data) ? data : []);
    } catch (e: any) {
      toast({ title: 'Erreur', description: 'Impossible de charger l\'historique des relances.', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    void load();
  }, [invoiceId]);

  const fmtDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleString('fr-FR', { 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  if (busy) {
    return (
      <div className="p-4 text-center text-slate-500">
        Chargement de l'historique...
      </div>
    );
  }

  if (reminders.length === 0) {
    return (
      <div className="p-6 text-center">
        <Mail className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
        <p className="text-slate-500 dark:text-slate-400">Aucune relance envoyée</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {reminders.map((reminder) => {
        const statusConfig = getStatusConfig(reminder.status);
        const StatusIcon = statusConfig.icon;
        const isExpanded = expandedId === reminder.id;

        return (
          <div 
            key={reminder.id} 
            className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden bg-white dark:bg-slate-900"
          >
            <div 
              className="p-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
              onClick={() => setExpandedId(isExpanded ? null : reminder.id)}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-semibold text-sm text-slate-900 dark:text-slate-100">
                      {getReminderTypeLabel(reminder.reminder_type)}
                    </span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig.color}`}>
                      <StatusIcon className="h-3 w-3" />
                      {statusConfig.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-slate-600 dark:text-slate-400">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {fmtDate(reminder.sent_at)}
                    </span>
                    <span>→ {reminder.email_sent_to}</span>
                  </div>
                  {reminder.subject && (
                    <p className="text-sm text-slate-700 dark:text-slate-300 mt-2 truncate">
                      {reminder.subject}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Expanded details */}
            {isExpanded && (
              <div className="px-4 pb-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                <div className="pt-3 space-y-3">
                  {/* Message */}
                  {reminder.message && (
                    <div>
                      <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Message</p>
                      <div className="p-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap font-mono">
                        {reminder.message}
                      </div>
                    </div>
                  )}

                  {/* Tracking info */}
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    {reminder.opened_at && (
                      <div>
                        <p className="text-slate-500 dark:text-slate-400">Ouverte le</p>
                        <p className="font-medium text-slate-900 dark:text-slate-100">{fmtDate(reminder.opened_at)}</p>
                      </div>
                    )}
                    {reminder.clicked_at && (
                      <div>
                        <p className="text-slate-500 dark:text-slate-400">Cliquée le</p>
                        <p className="font-medium text-slate-900 dark:text-slate-100">{fmtDate(reminder.clicked_at)}</p>
                      </div>
                    )}
                  </div>

                  {/* Notes internes */}
                  {reminder.notes && (
                    <div>
                      <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Notes internes</p>
                      <div className="p-2 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/30 rounded text-xs text-amber-900 dark:text-amber-300">
                        {reminder.notes}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ReminderHistoryPanel;
